'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../utils/cn';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
export function EnhancedDatePicker({ label, error, helperText, required, className, value, onChange, placeholder = "Select date...", minDate, maxDate, disabled = false, }) {
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState('days');
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [selectedDate, setSelectedDate] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    // Parse initial value
    useEffect(() => {
        if (value === null || value === undefined) {
            setSelectedDate(null);
            setInputValue('');
        }
        else if (value instanceof Date) {
            setSelectedDate(value);
            setInputValue(formatDate(value));
            setCurrentMonth(value.getMonth());
            setCurrentYear(value.getFullYear());
        }
        else if (typeof value === 'string') {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                setSelectedDate(date);
                setInputValue(formatDate(date));
                setCurrentMonth(date.getMonth());
                setCurrentYear(date.getFullYear());
            }
        }
    }, [value]);
    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setViewMode('days');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        // Try to parse the date
        if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const date = new Date(val);
            if (!isNaN(date.getTime())) {
                setSelectedDate(date);
                setCurrentMonth(date.getMonth());
                setCurrentYear(date.getFullYear());
                onChange?.(date);
            }
        }
    };
    const handleDateSelect = (day) => {
        const date = new Date(currentYear, currentMonth, day);
        setSelectedDate(date);
        setInputValue(formatDate(date));
        onChange?.(date);
        setIsOpen(false);
        setViewMode('days');
    };
    const handleMonthSelect = (monthIndex) => {
        setCurrentMonth(monthIndex);
        setViewMode('days');
    };
    const handleYearSelect = (year) => {
        setCurrentYear(year);
        setViewMode('months');
    };
    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };
    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };
    const renderDays = () => {
        const daysInMonth = getDaysInMonth(currentYear, currentMonth);
        const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
        const days = [];
        // Add empty cells for days before the first day
        for (let i = 0; i < firstDay; i++) {
            days.push(null);
        }
        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        return (_jsxs("div", { className: "p-4", children: [_jsx("div", { className: "grid grid-cols-7 gap-1 mb-2", children: DAYS.map((day, i) => (_jsx("div", { className: "text-center text-xs font-medium text-theme-secondary py-1", children: day }, i))) }), _jsx("div", { className: "grid grid-cols-7 gap-1", children: days.map((day, index) => {
                        if (day === null) {
                            return _jsx("div", {}, `empty-${index}`);
                        }
                        const isSelected = selectedDate &&
                            selectedDate.getDate() === day &&
                            selectedDate.getMonth() === currentMonth &&
                            selectedDate.getFullYear() === currentYear;
                        const isToday = new Date().getDate() === day &&
                            new Date().getMonth() === currentMonth &&
                            new Date().getFullYear() === currentYear;
                        return (_jsx("button", { type: "button", onClick: () => handleDateSelect(day), className: cn("w-8 h-8 text-sm rounded-md transition-colors", isSelected && "bg-primary-600 text-white font-medium", !isSelected && isToday && "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 font-medium", !isSelected && !isToday && "hover:bg-theme-tertiary text-theme-primary"), children: day }, day));
                    }) }), _jsxs("div", { className: "flex justify-between mt-4 pt-3 border-t border-theme-border", children: [_jsx("button", { type: "button", onClick: () => {
                                setSelectedDate(null);
                                setInputValue('');
                                onChange?.(null);
                                setIsOpen(false);
                            }, className: "text-sm text-primary-600 dark:text-primary-400 hover:underline", children: "Clear" }), _jsx("button", { type: "button", onClick: () => {
                                const today = new Date();
                                setSelectedDate(today);
                                setInputValue(formatDate(today));
                                setCurrentMonth(today.getMonth());
                                setCurrentYear(today.getFullYear());
                                onChange?.(today);
                                setIsOpen(false);
                            }, className: "text-sm text-primary-600 dark:text-primary-400 hover:underline", children: "Today" })] })] }));
    };
    const renderMonths = () => {
        return (_jsxs("div", { className: "p-4", children: [_jsx("div", { className: "mb-4", children: _jsx("button", { type: "button", onClick: () => setViewMode('years'), className: "w-full text-center text-sm font-medium text-theme-primary bg-theme-tertiary rounded-md py-2 hover:bg-theme-border transition-colors", children: currentYear }) }), _jsx("div", { className: "grid grid-cols-3 gap-2", children: MONTHS.map((month, index) => {
                        const isSelected = selectedDate &&
                            selectedDate.getMonth() === index &&
                            selectedDate.getFullYear() === currentYear;
                        const isCurrent = new Date().getMonth() === index &&
                            new Date().getFullYear() === currentYear;
                        return (_jsx("button", { type: "button", onClick: () => handleMonthSelect(index), className: cn("py-3 px-4 text-sm rounded-md transition-colors font-medium", isSelected && "bg-primary-600 text-white", !isSelected && isCurrent && "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300", !isSelected && !isCurrent && "hover:bg-theme-tertiary text-theme-primary"), children: month }, month));
                    }) })] }));
    };
    const renderYears = () => {
        // Show years from 100 years ago to 10 years in the future
        // This covers DOB for elderly people and future dates
        const thisYear = new Date().getFullYear();
        const startYear = thisYear - 100; // 100 years back for DOB
        const endYear = thisYear + 10; // 10 years ahead for future dates
        const years = [];
        for (let year = startYear; year <= endYear; year++) {
            years.push(year);
        }
        return (_jsx("div", { className: "p-4 max-h-80 overflow-y-auto", children: _jsx("div", { className: "grid grid-cols-3 gap-2", children: years.map((year) => {
                    const isSelected = selectedDate && selectedDate.getFullYear() === year;
                    const isCurrent = new Date().getFullYear() === year;
                    return (_jsx("button", { type: "button", onClick: () => handleYearSelect(year), className: cn("py-3 px-4 text-sm rounded-md transition-colors font-medium", isSelected && "bg-primary-600 text-white", !isSelected && isCurrent && "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300", !isSelected && !isCurrent && "hover:bg-theme-tertiary text-theme-primary"), children: year }, year));
                }) }) }));
    };
    const navigateMonth = (direction) => {
        if (direction === 'prev') {
            if (currentMonth === 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            }
            else {
                setCurrentMonth(currentMonth - 1);
            }
        }
        else {
            if (currentMonth === 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            }
            else {
                setCurrentMonth(currentMonth + 1);
            }
        }
    };
    return (_jsxs("div", { className: cn("space-y-1", className), ref: containerRef, children: [label && (_jsxs("label", { className: "block text-sm font-medium text-gray-700 dark:text-gray-200", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsxs("div", { className: "relative", children: [_jsx("input", { ref: inputRef, type: "text", value: inputValue, onChange: handleInputChange, onClick: () => !disabled && setIsOpen(!isOpen), placeholder: placeholder, disabled: disabled, className: cn("w-full h-10 px-3 pl-10 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500", error
                            ? "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-400"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-500") }), _jsx("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer", onClick: () => !disabled && setIsOpen(!isOpen), children: _jsx(Calendar, { className: cn("h-4 w-4", error ? "text-red-500" : "text-gray-400") }) }), isOpen && !disabled && (_jsxs("div", { className: "absolute z-50 mt-1 bg-theme-primary border border-theme-border rounded-lg shadow-lg min-w-[300px]", children: [viewMode === 'days' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-b border-theme-border", children: [_jsx("button", { type: "button", onClick: () => navigateMonth('prev'), className: "p-1 hover:bg-theme-tertiary rounded transition-colors", children: _jsx(ChevronLeft, { className: "h-5 w-5 text-theme-primary" }) }), _jsxs("button", { type: "button", onClick: () => setViewMode('months'), className: "text-sm font-medium text-theme-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors", children: [MONTHS[currentMonth], " ", currentYear] }), _jsx("button", { type: "button", onClick: () => navigateMonth('next'), className: "p-1 hover:bg-theme-tertiary rounded transition-colors", children: _jsx(ChevronRight, { className: "h-5 w-5 text-theme-primary" }) })] }), renderDays()] })), viewMode === 'months' && renderMonths(), viewMode === 'years' && renderYears()] }))] }), error && _jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error }), helperText && !error && _jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText })] }));
}
/**
 * Format a Date object to YYYY-MM-DD string in local timezone
 * This is useful for form submissions where the date should be sent as YYYY-MM-DD
 *
 * IMPORTANT: This function preserves the local timezone, unlike toISOString() which converts to UTC.
 * Always use this function instead of toISOString().split('T')[0] to avoid off-by-one date errors!
 *
 * @example
 * \ Instead of: date.toISOString().split('T')[0] (which converts to UTC)
 * // Use: formatDateToYYYYMMDD(date) (which keeps local timezone)
 *
 * const date = new Date(2025, 0, 15); // Jan 15, 2025 local time
 * formatDateToYYYYMMDD(date); // Returns "2025-01-15" (correct)
 * date.toISOString().split('T')[0]; // Returns "2025-01-14" (WRONG - UTC conversion issue)
 */
export function formatDateToYYYYMMDD(date) {
    if (!date)
        return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
export default EnhancedDatePicker;
